<template>
  <div class="vessel-map-component-container">
    <div class="map-view-container">
      <div v-show="showMap" class="w-full h-full">
        <div id="map-view" class="w-full h-full"></div>
      </div>
      <div v-show="!showMap">
        <div class="no-map-view flex items-center justify-center">
          航路情報が見つかりません。
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Mapbox from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import LocalDbService from "@/services/localDbService";
import * as turf from "@turf/turf";
import * as MapUtil from "@/utils/MapUtil";
import { createNamespacedHelpers } from "vuex";
const { mapGetters, mapState } = createNamespacedHelpers("fuelConsumption");

// Mixins
import {
  LocalizeMixin,
  DeviceInfoMixin,
  LayerMixin,
  ColorMixin,
} from "@/mixins";
import { MAP_COLORS } from "../../../Constants.js";
export default {
  mixins: [LocalizeMixin, DeviceInfoMixin, LayerMixin, ColorMixin],

  props: {
    showMap: {
      type: Boolean,
      required: true,
    },
  },

  watch: {
    currentGraphSyncLine(newFeature, oldFeature) {
      if (newFeature.length === 0 || !newFeature.properties.hasCoordinates) {
        this.$vs.notify({
          title: `位置情報がないため、マップと同期ができませんでした。`,
          color: "warning",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
        return;
      }
      const geojson = {
        type: "FeatureCollection",
        features: [newFeature],
      };
      let currentZoom = this.map.getZoom();
      this.map.getSource("graph-sync-line").setData(geojson);
      let index = Math.floor(newFeature.geometry.coordinates.length / 2);
      this.map.easeTo({
        center: newFeature.geometry.coordinates[index],
        zoom: currentZoom < 9 ? 9 : currentZoom,
        speed: 0.2,
        curve: 3,
        duration: 500,
      });
    },
  },

  data() {
    return {
      map: null,
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      defaultZoom: 3,
      mapStyleLocal: null,
      center: [133, 35],
      popup: null,
      pointCircleRadius: 8,
      expandedPointCircleRadius: 12,
    };
  },

  computed: {
    ...mapState({
      actualRouteLinesFeatures: state => state.actualRouteLinesFeatures,
      currentGraphSyncLine: state => state.currentGraphSyncLine,
    }),
    ...mapGetters([
      "getMapCenterPosition",
      "getMapInitialPosition",
      "getActualRouteLinesFeatures",
      "getGraphSyncLine",
      "getRouteFeatureCollection",
    ]),
  },

  methods: {
    async initMap() {
      try {
        this.mapStyleLocal = await LocalDbService.getMapStyle();
        Mapbox.accessToken = this.accessToken;
        this.map = new Mapbox.Map({
          attributionControl: false,
          container: "map-view",
          style: this.mapStyleLocal,
          center: [139.86448000000001, 35.54817666666667],
          zoom: 5,
        });
        this.map.getCanvas().style.cursor = "crosshair";
        this.map.on("load", this.onMapLoad);
      } catch (error) {
        this.$vs.notify({
          title: "マップ情報の取得に失敗しました。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
        throw error;
      }
    },

    onMapLoad() {
      this.addWeatherRoutingLayer();
      this.addActualRouteLineLayer();
      this.addAverageLineLayer();
      this.setLanguage();
      this.adjustZoomLevel();
    },

    addWeatherRoutingLayer() {
      const data = this.getRouteFeatureCollection;
      let weatherRoutingSourceId = "weather-routing-source";
      if (data.features.length !== 0) {
        this.map.addSource(weatherRoutingSourceId, {
          type: "geojson",
          data: data,
        });
        for (const feature of data.features) {
          let geom = turf.getType(feature);
          if (geom === "LineString") {
            const routeType = feature.properties.routeType;
            const routeColor = feature.properties.routeColor;
            const layerID = `line-${routeType}`;
            this.map.addLayer({
              id: layerID,
              type: "line",
              source: weatherRoutingSourceId,
              layout: { "line-cap": "round" },
              paint: {
                "line-color": routeColor,
                "line-width": 2,
              },
              filter: ["==", "routeType", routeType],
            });
          }
        }

        let waypointsLayerId = "wr-recommend-route-waypoints";
        this.map.addLayer({
          id: waypointsLayerId,
          type: "circle",
          minzoom: 8,
          source: weatherRoutingSourceId,
          paint: {
            "circle-color": "#FFF",
            "circle-radius": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              8,
              5,
            ],
            "circle-stroke-color": ["get", "pointColor"],
            "circle-stroke-width": 2,
          },
          filter: [
            "any",
            ["==", "pointType", "recommended"],
            ["==", "pointType", "usual"],
          ],
        });

        const popup = new Mapbox.Popup({
          offset: 5,
          closeButton: false,
          closeOnClick: false,
        });

        let hoverPointId = null;
        this.map.on("mouseenter", waypointsLayerId, e => {
          if (e.features.length > 0) {
            if (hoverPointId !== null) {
              this.map.setFeatureState(
                { source: weatherRoutingSourceId, id: hoverPointId },
                { hover: false }
              );
            }
            hoverPointId = e.features[0].id;
            this.map.setFeatureState(
              { source: weatherRoutingSourceId, id: hoverPointId },
              { hover: true }
            );
          }

          this.map.setFeatureState(
            { source: weatherRoutingSourceId, id: e.features[0].id },
            { hover: true }
          );

          this.map.getCanvas().style.cursor = "pointer";
          const coordinates = e.features[0].geometry.coordinates.slice();
          const description = e.features[0].properties.popupHtml;
          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }
          popup
            .setLngLat(coordinates)
            .setHTML(description)
            .addTo(this.map);
        });

        this.map.on("mouseleave", waypointsLayerId, () => {
          if (hoverPointId !== null) {
            this.map.setFeatureState(
              { source: weatherRoutingSourceId, id: hoverPointId },
              { hover: false }
            );
          }
          hoverPointId = null;
          this.map.getCanvas().style.cursor = "";
          popup.remove();
        });
      }
    },

    addActualRouteLineLayer() {
      let actualRouteSourceId = "actual-route-line-source";
      const features = this.getActualRouteLinesFeatures;
      this.map.addSource(actualRouteSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: features,
        },
      });

      this.map.addLayer({
        type: "line",
        source: actualRouteSourceId,
        id: "actual-route-line-layer",
        paint: {
          "line-color": [
            "case",
            ["boolean", ["get", "isMissingLine"], true],
            ["literal", "#949593"],
            ["literal", "#1BA9E9"],
          ],
          "line-width": 2,
          "line-dasharray": [
            "case",
            ["boolean", ["get", "isMissingLine"], true],
            ["literal", [1, 1]],
            ["literal", [1]],
          ],
        },
      });

      this.map.addLayer({
        id: "actual-route-points",
        type: "circle",
        source: actualRouteSourceId,
        paint: {
          "circle-color": "#FFF",
          "circle-radius": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            10,
            7,
          ],
          "circle-stroke-color": MAP_COLORS.ACTUAL,
          "circle-stroke-width": 3,
        },
        filter: [
          "any",
          ["==", "pointType", "start"],
          ["==", "pointType", "goal"],
        ],
      });

      const popup = new Mapbox.Popup({
        offset: 5,
        closeButton: false,
        closeOnClick: false,
      });

      let hoverPointId = null;
      this.map.on("mouseenter", "actual-route-points", e => {
        if (e.features.length > 0) {
          if (hoverPointId !== null) {
            this.map.setFeatureState(
              { source: actualRouteSourceId, id: hoverPointId },
              { hover: false }
            );
          }
          hoverPointId = e.features[0].id;
          this.map.setFeatureState(
            { source: actualRouteSourceId, id: hoverPointId },
            { hover: true }
          );
        }

        this.map.setFeatureState(
          { source: actualRouteSourceId, id: e.features[0].id },
          { hover: true }
        );

        this.map.getCanvas().style.cursor = "pointer";
        const coordinates = e.features[0].geometry.coordinates.slice();
        const description = e.features[0].properties.popupHtml;
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        popup
          .setLngLat(coordinates)
          .setHTML(description)
          .addTo(this.map);
      });

      this.map.on("mouseleave", "actual-route-points", () => {
        if (hoverPointId !== null) {
          this.map.setFeatureState(
            { source: actualRouteSourceId, id: hoverPointId },
            { hover: false }
          );
        }
        hoverPointId = null;
        this.map.getCanvas().style.cursor = "";
        popup.remove();
      });
    },

    addAverageLineLayer() {
      this.map.addSource("graph-sync-line", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addLayer({
        type: "line",
        source: "graph-sync-line",
        id: "line-background",
        paint: {
          "line-color": "#FFCB05",
          "line-width": 6,
          "line-opacity": 0.4,
        },
      });

      this.map.addLayer({
        type: "line",
        source: "graph-sync-line",
        id: "line-dashed",
        paint: {
          "line-color": "#FFCB05",
          "line-width": 6,
          "line-dasharray": [0, 4, 3],
        },
      });

      const dashArraySequence = [
        [0, 4, 3],
        [0.5, 4, 2.5],
        [1, 4, 2],
        [1.5, 4, 1.5],
        [2, 4, 1],
        [2.5, 4, 0.5],
        [3, 4, 0],
        [0, 0.5, 3, 3.5],
        [0, 1, 3, 3],
        [0, 1.5, 3, 2.5],
        [0, 2, 3, 2],
        [0, 2.5, 3, 1.5],
        [0, 3, 3, 1],
        [0, 3.5, 3, 0.5],
      ];

      let step = 0;
      let self = this;
      function animateDashArray(timestamp) {
        const newStep = parseInt((timestamp / 50) % dashArraySequence.length);

        if (newStep !== step) {
          self.map.setPaintProperty(
            "line-dashed",
            "line-dasharray",
            dashArraySequence[step]
          );
          step = newStep;
        }
        requestAnimationFrame(animateDashArray);
      }
      animateDashArray(0);
    },

    setLanguage() {
      if (!this.isDefaultMapStyle(this.mapStyleLocal)) return;
      let lang = this.getBrowserLanguage();
      let local = "name_" + lang;
      if (!this.map) return;
      let textLayerIds = this.getTextLayerIds();
      textLayerIds.forEach(id => {
        this.map.setLayoutProperty(id, "text-field", ["get", local]);
      });
    },

    adjustZoomLevel() {
      let mapInitialPosition = this.getMapInitialPosition;
      this.map.easeTo({
        center: mapInitialPosition.center,
        zoom: mapInitialPosition.zoom,
        speed: 0.2,
        curve: 3,
        duration: 500,
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";
@import "@/assets/scss/aisea/_typography.scss";
@import "src/assets/scss/aisea/fuel-consumption";

$view-height: 320px;

.vessel-map-component-container {
  .map-view-container {
    background: $aisea-dark-blue !important;
    height: $view-height;
    border-radius: 8px;
    #map-view {
      border-radius: 8px;
      .mapboxgl-ctrl-bottom-left {
        display: none;
      }
    }

    .mapboxgl-popup-content {
      .headLine {
        font-size: 1rem;
        font-weight: 500;
        line-height: 2rem;
      }
      opacity: 0.9;
      font-weight: 400;
      line-height: 1.5rem;
      border-radius: 3px;
      box-shadow: 6px 6px 10px 0 rgba(0, 0, 0, 0.4);
    }

    .mapboxgl-popup-tip {
      visibility: hidden;
    }
  }

  .mapboxgl-popup-close-button {
    display: none;
  }

  .no-map-view {
    width: 100%;
    height: $view-height;
    background-color: $aisea-dark-blue;
    color: $aisea-pure-white;
    font-size: 24px;
    border-radius: 8px;
    p {
      text-align: center;
    }
  }
}
</style>
